import { useCallback, useMemo, useState } from "react";

import { DEFAULT_MODEL, REASONING_EFFORT_DEFAULT } from "../../../utils/ModelConfig";
import {
  AI_AGENT_DEFAULT_NAME,
  CREATE_PATH_AI,
  CREATE_PATH_MANUAL,
  CREATE_STAGE_IDLE,
  CREATE_STAGE_CREATING_AGENT,
  CREATE_STAGE_STARTING_CHAT,
  normalizeAiRequirementsInput,
} from "./agentCreation";
import {
  DEFAULT_EMAIL_RESPONSE_POLICY_PRESET,
  buildWebhookPayloadPreview,
} from "./emailPolicy";
import {
  EXTERNAL_CHAT_DEFAULT_ACCESS_POLICY,
  buildExternalAccessPolicy,
  EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT,
  EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE,
} from "./externalAccess";
import {
  KNOWLEDGE_INDEX_DEFAULT_STATUS,
  normalizeKnowledgeIndexStatus,
} from "./knowledgeStatus";
import { SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT } from "./selfConfiguration";
import { DEFAULT_ENABLED_TOOLS, TABLE_SCOPE_TOOL_NAMES } from "./toolCatalog";

export function useAgentConfigDraft({ isCreateMode }) {
  const [isSystemAgent, setIsSystemAgent] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [reasoningEffort, setReasoningEffort] = useState(REASONING_EFFORT_DEFAULT);
  const [maxIterations, setMaxIterations] = useState(50);
  const [selfConfigurationMode, setSelfConfigurationMode] = useState(
    SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
  );
  const [enabledTools, setEnabledTools] = useState([...DEFAULT_ENABLED_TOOLS]);
  const [toolInstructions, setToolInstructions] = useState({});
  const [sendSlackWebhookUrlsInput, setSendSlackWebhookUrlsInput] =
    useState("");
  const [sendSlackWebhookValidationError, setSendSlackWebhookValidationError] =
    useState("");
  const [toolCategories, setToolCategories] = useState([]);

  const tableScopedToolNames = useMemo(() => {
    const set = new Set(TABLE_SCOPE_TOOL_NAMES);
    toolCategories.forEach((cat) =>
      (cat.tools || []).forEach((t) => {
        if (t.requiresTableScope) set.add(t.name);
      })
    );
    return set;
  }, [toolCategories]);
  const [toolSearchQuery, setToolSearchQuery] = useState("");
  const [loadingToolCatalog, setLoadingToolCatalog] = useState(false);
  const [runWorkflowConfigs, setRunWorkflowConfigs] = useState([]);
  const [workflowScopeMode, setWorkflowScopeMode] = useState("SELECTED");
  const [runWorkflowValidationError, setRunWorkflowValidationError] =
    useState("");
  const [configuredTables, setConfiguredTables] = useState([]);
  const [configuredAgents, setConfiguredAgents] = useState([]);
  const [tableScopeMode, setTableScopeMode] = useState("SELECTED");
  const [agentScopeMode, setAgentScopeMode] = useState("SELECTED");
  const [fileScopeMode, setFileScopeMode] = useState("SELECTED");
  const [artifactScopeMode, setArtifactScopeMode] = useState("SELECTED");
  const [configuredTablesValidationError, setConfiguredTablesValidationError] =
    useState("");
  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [loadingWorkflowOptions, setLoadingWorkflowOptions] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  const [loadingAgentOptions, setLoadingAgentOptions] = useState(false);
  const [completionNotificationType, setCompletionNotificationType] =
    useState("NONE");
  const [completionNotifyEmails, setCompletionNotifyEmails] = useState("");
  const [completionSlackWebhookUrl, setCompletionSlackWebhookUrl] =
    useState("");
  const [completionWebhookUrl, setCompletionWebhookUrl] = useState("");
  const knowledgeRetrievalMode = "RAG";
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [knowledgeTables, setKnowledgeTables] = useState([]);
  const [knowledgeManualContext, setKnowledgeManualContext] = useState("");
  const [knowledgeIndexStatus, setKnowledgeIndexStatus] = useState(
    KNOWLEDGE_INDEX_DEFAULT_STATUS
  );
  const [inboxAddress, setInboxAddress] = useState("");
  const [allowAllSenders, setAllowAllSenders] = useState(false);
  const [acceptEmailAttachments, setAcceptEmailAttachments] = useState(false);
  const [senderWhitelist, setSenderWhitelist] = useState("");
  const [emailResponsePolicyPreset, setEmailResponsePolicyPreset] =
    useState("ALWAYS_REPLY_EMAIL");
  const [emailResponsePolicyCustomPrompt, setEmailResponsePolicyCustomPrompt] =
    useState("");
  const [externalAccessPolicy, setExternalAccessPolicy] = useState(
    EXTERNAL_CHAT_DEFAULT_ACCESS_POLICY
  );
  const [
    externalChatMaxSessionsPerIdentity,
    setExternalChatMaxSessionsPerIdentity,
  ] = useState("");
  const [externalChatMaxSessionsTotal, setExternalChatMaxSessionsTotal] =
    useState("");
  const [externalChatRateLimitMessages, setExternalChatRateLimitMessages] =
    useState("");
  const [
    externalChatRateLimitPeriodValue,
    setExternalChatRateLimitPeriodValue,
  ] = useState(EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE);
  const [externalChatRateLimitPeriodUnit, setExternalChatRateLimitPeriodUnit] =
    useState(EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT);
  const [
    externalChatRateLimitExceededMessage,
    setExternalChatRateLimitExceededMessage,
  ] = useState("");
  const [reviewEscalationPolicy, setReviewEscalationPolicy] = useState("");
  const [blockedWhileReviewMessage, setBlockedWhileReviewMessage] =
    useState("");
  const [
    customBlockedWhileReviewMessageEnabled,
    setCustomBlockedWhileReviewMessageEnabled,
  ] = useState(false);
  const [autoReplyOnBlockedIngress, setAutoReplyOnBlockedIngress] =
    useState(true);
  const [reviewTaskAssigneeUserId, setReviewTaskAssigneeUserId] = useState("");
  const [
    reviewTaskEmailNotificationEnabled,
    setReviewTaskEmailNotificationEnabled,
  ] = useState(false);
  const [reviewTaskEligibleUsers, setReviewTaskEligibleUsers] = useState([]);
  const [loadingReviewTaskEligibleUsers, setLoadingReviewTaskEligibleUsers] =
    useState(false);
  const [publicShareLinkExpiresAt, setPublicShareLinkExpiresAt] = useState("");
  const [enablePublicShareLinkExpiry, setEnablePublicShareLinkExpiry] =
    useState(false);
  const [showSystemPromptInChat, setShowSystemPromptInChat] = useState(true);
  const [showToolCallsAndOutputsInChat, setShowToolCallsAndOutputsInChat] =
    useState(true);
  const [showInterAgentMessagesInChat, setShowInterAgentMessagesInChat] =
    useState(true);
  const [showWorkflowProvenanceInChat, setShowWorkflowProvenanceInChat] =
    useState(true);
  const [showSchedulingActivityInChat, setShowSchedulingActivityInChat] =
    useState(true);
  const [
    showSessionOperationalMetaInChat,
    setShowSessionOperationalMetaInChat,
  ] = useState(true);
  const [
    showSupervisorControlMessagesInChat,
    setShowSupervisorControlMessagesInChat,
  ] = useState(true);
  const [enableHumanTakeoverMode, setEnableHumanTakeoverMode] = useState(false);
  const [showSharedSessionsSidebar, setShowSharedSessionsSidebar] =
    useState(true);
  const [examplePrompts, setExamplePrompts] = useState([]);
  const [newWhitelistEntry, setNewWhitelistEntry] = useState("");
  const [databaseList, setDatabaseList] = useState([]);
  const [loadingDatabases, setLoadingDatabases] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [creationPath, setCreationPath] = useState(null);
  const [aiRequirements, setAiRequirements] = useState("");
  const [creationStage, setCreationStage] = useState(CREATE_STAGE_IDLE);
  const [creationError, setCreationError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [globalVarPlaceholders, setGlobalVarPlaceholders] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const webhookPayloadPreview = buildWebhookPayloadPreview(name);
  const isAiCreatePath = isCreateMode && creationPath === CREATE_PATH_AI;
  const isManualCreatePath =
    isCreateMode && creationPath === CREATE_PATH_MANUAL;
  const showCreationPathChooser = isCreateMode && !creationPath;
  const showManualConfigurationLayout = !isCreateMode || isManualCreatePath;
  const normalizedAiRequirements = normalizeAiRequirementsInput(aiRequirements);

  const applyDraftToLocalState = useCallback((draft, publicShareState = {}) => {
    const identity = draft.identity || {};
    const tools = draft.tools || {};
    const completion = draft.completion || {};
    const knowledge = draft.knowledge || {};
    const email = draft.email || {};
    const externalChat = draft.externalChat || {};
    const review = draft.review || {};
    const publicShare = draft.publicShare || {};
    const {
      setPublicShareLink,
      setPublicShareLinkError,
      setPublicShareLinkExpiresAt,
      setEnablePublicShareLinkExpiry,
    } = publicShareState;
    const chatUi = draft.chatUi || {};
    const creation = draft.creation || {};
    const ui = draft.ui || {};

    setIsSystemAgent(identity.isSystemAgent === true);
    setName(identity.name || "");
    setDescription(identity.description || "");
    setSystemPrompt(identity.systemPrompt || "");
    setModel(identity.model || DEFAULT_MODEL);
    setReasoningEffort(identity.reasoningEffort || REASONING_EFFORT_DEFAULT);
    setMaxIterations(identity.maxIterations || 50);
    setSelfConfigurationMode(
      tools.selfConfigurationMode || SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
    );
    setEnabledTools(tools.enabledTools || [...DEFAULT_ENABLED_TOOLS]);
    setToolInstructions(tools.toolInstructions || {});
    setSendSlackWebhookUrlsInput(tools.sendSlackWebhookUrlsInput || "");
    setSendSlackWebhookValidationError("");
    setRunWorkflowConfigs(tools.runWorkflowConfigs || []);
    setWorkflowScopeMode(tools.workflowScopeMode || "SELECTED");
    setRunWorkflowValidationError("");
    setConfiguredTables(tools.configuredTables || []);
    setConfiguredAgents(tools.configuredAgents || []);
    setExamplePrompts(draft.examplePrompts || []);
    setTableScopeMode(tools.tableScopeMode || "SELECTED");
    setAgentScopeMode(tools.agentScopeMode || "SELECTED");
    setFileScopeMode(tools.fileScopeMode || "SELECTED");
    setArtifactScopeMode(tools.artifactScopeMode || "SELECTED");
    setConfiguredTablesValidationError("");
    setCompletionNotificationType(completion.notificationType || "NONE");
    setCompletionNotifyEmails(completion.notifyEmails || "");
    setCompletionSlackWebhookUrl(completion.slackWebhookUrl || "");
    setCompletionWebhookUrl(completion.webhookUrl || "");
    setKnowledgeFiles(knowledge.files || []);
    setKnowledgeTables(knowledge.tables || []);
    setKnowledgeManualContext(knowledge.manualContext || "");
    setKnowledgeIndexStatus(
      normalizeKnowledgeIndexStatus(knowledge.indexStatus)
    );
    setInboxAddress(email.inboxAddress || "");
    setAllowAllSenders(email.allowAllSenders === true);
    setAcceptEmailAttachments(email.acceptEmailAttachments === true);
    setSenderWhitelist(email.senderWhitelist || "");
    setEmailResponsePolicyPreset(
      email.responsePolicyPreset || DEFAULT_EMAIL_RESPONSE_POLICY_PRESET
    );
    setEmailResponsePolicyCustomPrompt(email.responsePolicyCustomPrompt || "");
    setExternalAccessPolicy(
      buildExternalAccessPolicy(externalChat.accessPolicy)
    );
    setExternalChatMaxSessionsPerIdentity(
      externalChat.maxSessionsPerIdentity || ""
    );
    setExternalChatMaxSessionsTotal(externalChat.maxSessionsTotal || "");
    setExternalChatRateLimitMessages(externalChat.rateLimitMessages || "");
    setExternalChatRateLimitPeriodValue(
      externalChat.rateLimitPeriodValue ||
        EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE
    );
    setExternalChatRateLimitPeriodUnit(
      externalChat.rateLimitPeriodUnit ||
        EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT
    );
    setExternalChatRateLimitExceededMessage(
      externalChat.rateLimitExceededMessage || ""
    );
    setReviewEscalationPolicy(review.escalationPolicy || "");
    setBlockedWhileReviewMessage(review.blockedWhileReviewMessage || "");
    setCustomBlockedWhileReviewMessageEnabled(
      review.customBlockedWhileReviewMessageEnabled === true
    );
    setAutoReplyOnBlockedIngress(review.autoReplyOnBlockedIngress !== false);
    setReviewTaskAssigneeUserId(review.assigneeUserId || "");
    setReviewTaskEmailNotificationEnabled(
      review.emailNotificationEnabled === true
    );
    setPublicShareLink?.(publicShare.link || null);
    setPublicShareLinkError?.(publicShare.error || "");
    setPublicShareLinkExpiresAt?.(publicShare.expiresAt || "");
    setEnablePublicShareLinkExpiry?.(publicShare.enableExpiry === true);
    setShowSystemPromptInChat(chatUi.showSystemPromptInChat !== false);
    setShowToolCallsAndOutputsInChat(
      chatUi.showToolCallsAndOutputsInChat !== false
    );
    setShowInterAgentMessagesInChat(
      chatUi.showInterAgentMessagesInChat !== false
    );
    setShowWorkflowProvenanceInChat(
      chatUi.showWorkflowProvenanceInChat !== false
    );
    setShowSchedulingActivityInChat(
      chatUi.showSchedulingActivityInChat !== false
    );
    setShowSessionOperationalMetaInChat(
      chatUi.showSessionOperationalMetaInChat !== false
    );
    setShowSupervisorControlMessagesInChat(
      chatUi.showSupervisorControlMessagesInChat !== false
    );
    setEnableHumanTakeoverMode(chatUi.enableHumanTakeoverMode === true);
    setShowSharedSessionsSidebar(chatUi.showSharedSessionsSidebar !== false);
    setSaveResult(ui.saveResult || null);
    setSaveErrorMessage(ui.saveErrorMessage || "");
    setCreationPath(creation.path || null);
    setAiRequirements(creation.aiRequirements || "");
    setCreationStage(creation.stage || CREATE_STAGE_IDLE);
    setCreationError(creation.error || "");
    setDeleteConfirmText(ui.deleteConfirmText || "");
  }, []);

  return {
    isSystemAgent,
    setIsSystemAgent,
    name,
    setName,
    description,
    setDescription,
    deleteConfirmText,
    setDeleteConfirmText,
    deleting,
    setDeleting,
    systemPrompt,
    setSystemPrompt,
    model,
    setModel,
    reasoningEffort,
    setReasoningEffort,
    maxIterations,
    setMaxIterations,
    selfConfigurationMode,
    setSelfConfigurationMode,
    enabledTools,
    setEnabledTools,
    toolInstructions,
    setToolInstructions,
    sendSlackWebhookUrlsInput,
    setSendSlackWebhookUrlsInput,
    sendSlackWebhookValidationError,
    setSendSlackWebhookValidationError,
    toolCategories,
    setToolCategories,
    tableScopedToolNames,
    toolSearchQuery,
    setToolSearchQuery,
    loadingToolCatalog,
    setLoadingToolCatalog,
    runWorkflowConfigs,
    setRunWorkflowConfigs,
    workflowScopeMode,
    setWorkflowScopeMode,
    runWorkflowValidationError,
    setRunWorkflowValidationError,
    configuredTables,
    setConfiguredTables,
    configuredAgents,
    setConfiguredAgents,
    tableScopeMode,
    setTableScopeMode,
    agentScopeMode,
    setAgentScopeMode,
    fileScopeMode,
    setFileScopeMode,
    artifactScopeMode,
    setArtifactScopeMode,
    configuredTablesValidationError,
    setConfiguredTablesValidationError,
    workflowOptions,
    setWorkflowOptions,
    loadingWorkflowOptions,
    setLoadingWorkflowOptions,
    agentOptions,
    setAgentOptions,
    loadingAgentOptions,
    setLoadingAgentOptions,
    completionNotificationType,
    setCompletionNotificationType,
    completionNotifyEmails,
    setCompletionNotifyEmails,
    completionSlackWebhookUrl,
    setCompletionSlackWebhookUrl,
    completionWebhookUrl,
    setCompletionWebhookUrl,
    knowledgeRetrievalMode,
    knowledgeFiles,
    setKnowledgeFiles,
    knowledgeTables,
    setKnowledgeTables,
    knowledgeManualContext,
    setKnowledgeManualContext,
    knowledgeIndexStatus,
    setKnowledgeIndexStatus,
    inboxAddress,
    setInboxAddress,
    allowAllSenders,
    setAllowAllSenders,
    acceptEmailAttachments,
    setAcceptEmailAttachments,
    senderWhitelist,
    setSenderWhitelist,
    emailResponsePolicyPreset,
    setEmailResponsePolicyPreset,
    emailResponsePolicyCustomPrompt,
    setEmailResponsePolicyCustomPrompt,
    externalAccessPolicy,
    setExternalAccessPolicy,
    externalChatMaxSessionsPerIdentity,
    setExternalChatMaxSessionsPerIdentity,
    externalChatMaxSessionsTotal,
    setExternalChatMaxSessionsTotal,
    externalChatRateLimitMessages,
    setExternalChatRateLimitMessages,
    externalChatRateLimitPeriodValue,
    setExternalChatRateLimitPeriodValue,
    externalChatRateLimitPeriodUnit,
    setExternalChatRateLimitPeriodUnit,
    externalChatRateLimitExceededMessage,
    setExternalChatRateLimitExceededMessage,
    reviewEscalationPolicy,
    setReviewEscalationPolicy,
    blockedWhileReviewMessage,
    setBlockedWhileReviewMessage,
    customBlockedWhileReviewMessageEnabled,
    setCustomBlockedWhileReviewMessageEnabled,
    autoReplyOnBlockedIngress,
    setAutoReplyOnBlockedIngress,
    reviewTaskAssigneeUserId,
    setReviewTaskAssigneeUserId,
    reviewTaskEmailNotificationEnabled,
    setReviewTaskEmailNotificationEnabled,
    reviewTaskEligibleUsers,
    setReviewTaskEligibleUsers,
    loadingReviewTaskEligibleUsers,
    setLoadingReviewTaskEligibleUsers,
    publicShareLinkExpiresAt,
    setPublicShareLinkExpiresAt,
    enablePublicShareLinkExpiry,
    setEnablePublicShareLinkExpiry,
    showSystemPromptInChat,
    setShowSystemPromptInChat,
    showToolCallsAndOutputsInChat,
    setShowToolCallsAndOutputsInChat,
    showInterAgentMessagesInChat,
    setShowInterAgentMessagesInChat,
    showWorkflowProvenanceInChat,
    setShowWorkflowProvenanceInChat,
    showSchedulingActivityInChat,
    setShowSchedulingActivityInChat,
    showSessionOperationalMetaInChat,
    setShowSessionOperationalMetaInChat,
    showSupervisorControlMessagesInChat,
    setShowSupervisorControlMessagesInChat,
    enableHumanTakeoverMode,
    setEnableHumanTakeoverMode,
    showSharedSessionsSidebar,
    setShowSharedSessionsSidebar,
    examplePrompts,
    setExamplePrompts,
    newWhitelistEntry,
    setNewWhitelistEntry,
    databaseList,
    setDatabaseList,
    loadingDatabases,
    setLoadingDatabases,
    activeTab,
    setActiveTab,
    saving,
    setSaving,
    saveResult,
    setSaveResult,
    saveErrorMessage,
    setSaveErrorMessage,
    creationPath,
    setCreationPath,
    aiRequirements,
    setAiRequirements,
    creationStage,
    setCreationStage,
    creationError,
    setCreationError,
    loaded,
    setLoaded,
    globalVarPlaceholders,
    setGlobalVarPlaceholders,
    allAgents,
    setAllAgents,
    webhookPayloadPreview,
    isAiCreatePath,
    isManualCreatePath,
    showCreationPathChooser,
    showManualConfigurationLayout,
    normalizedAiRequirements,
    applyDraftToLocalState,
  };
}

export function buildAgentConfigChromeState({
  isCreateMode,
  loaded,
  showCreationPathChooser,
  isAiCreatePath,
  name,
  saving,
  saveResult,
  normalizedAiRequirements,
  creationStage,
}) {
  const isEditLoading = !isCreateMode && !loaded;
  const headerTitle = isEditLoading
    ? "Loading…"
    : isCreateMode
    ? showCreationPathChooser
      ? "New Agent"
      : isAiCreatePath
      ? AI_AGENT_DEFAULT_NAME
      : name || "New Agent"
    : name || "Agent Configuration";
  const headerSubtitle = isEditLoading
    ? ""
    : isCreateMode
    ? showCreationPathChooser
      ? "Choose how you want to set up this agent."
      : isAiCreatePath
      ? "Create from natural-language requirements."
      : "Set up your new agent."
    : "Configure the agent's identity, model, and available tools.";
  const saveButtonDisabled =
    saving ||
    saveResult === "success" ||
    (showCreationPathChooser
      ? true
      : isAiCreatePath
      ? !normalizedAiRequirements
      : !name.trim());
  const saveButtonLabel =
    creationStage === CREATE_STAGE_CREATING_AGENT
      ? "Creating agent..."
      : creationStage === CREATE_STAGE_STARTING_CHAT
      ? "Starting first chat..."
      : saving
      ? "Saving..."
      : saveResult === "success"
      ? "Saved"
      : saveResult === "error"
      ? "Failed"
      : isAiCreatePath
      ? "Create & Start Chat"
      : isCreateMode
      ? "Create"
      : "Save";
  const avatarName = isEditLoading
    ? "…"
    : isCreateMode && isAiCreatePath
    ? AI_AGENT_DEFAULT_NAME
    : name || "New Agent";

  return {
    isEditLoading,
    headerTitle,
    headerSubtitle,
    saveButtonDisabled,
    saveButtonLabel,
    avatarName,
  };
}
